import type { GoalDodRow } from './goal-dod-rows';
// ============================================
// Frontend Type Definitions
// Mirrors src/types.ts for browser-side usage
// ============================================

export interface User {
  id: string;
  email: string;
  name: string | null;
  created_at: string;
}

export interface UserProfile {
  id: string;
  user_id: string;
  email?: string | null;
  display_names: string[];
  role: string | null;
  interests: string[] | null;
  current_goals: string[] | null;
  location: string | null;
  timezone: string | null;
  language: string | null;
  preferences: ProfilePreferences | null;
  writing_style?: Record<string, unknown> | null;
  sync_rules?: Record<string, unknown> | null;
  last_sync_at?: string | null;
  last_sync_summary?: string | null;
  last_sync_status?: string | null;
  updated_at: string;
}

export type AccountingExecutor = "self" | "delegate" | "none";
export type AccountingSystem = "freee" | "moneyforward" | "excel" | "other";

export interface AccountingTask {
  executor: AccountingExecutor;
  system?: AccountingSystem | null;
}

export interface AccountingWhere {
  payments?: AccountingTask | null;
  invoices?: AccountingTask | null;
  expenses?: AccountingTask | null;
}

export interface ProfileWhere {
  accounting?: AccountingWhere | null;
  mail?: "gmail" | "outlook" | "other" | null;
  chat?: "slack" | "teams" | "line_works" | null;
  schedule?: "google_calendar" | "outlook_calendar" | "paper" | null;
}

export interface ProfilePreferences {
  where?: ProfileWhere;
  comm_external?: "formal" | "business_casual" | "casual" | "auto";
  comm_internal?: "formal" | "casual" | "auto";
  task_granularity?: "fine" | "coarse" | "normal";
  travel_mode?: "train" | "car" | "plane" | "undecided" | "bike";
  /** #2817: 拠点2; 拠点1 is `profiles.location`. */
  second_location?: string | null;
  /** #2817: the base used on a weekday daytime; the other base at other times. Unset reads as `first`. */
  weekday_daytime_location?: "first" | "second";
  meeting_default_attendance?: "in_person" | "online" | "auto";
  event_default_attendance?: "in_person" | "online" | "auto";
  /** #2320: the deadline a Goal gets when none was read. Unset reads as `same_day`. */
  unread_deadline_default?: "same_day" | "next_business_day";
  timezone?: string | null;
  [key: string]: unknown;
}

export type Priority = 'high' | 'medium' | 'low';
export type SubtaskStatus = 'pending' | 'in_progress' | 'review' | 'done' | 'blocked' | 'invalidated';
export type SubtaskInvalidatedReason = 'parent_goal_done' | 'parent_goal_deleted' | 'superseded';
export type SubtaskTerminalReason = 'parent_goal_closed' | 'plan_changed' | 'projection_changed' | 'duplicate' | 'manual';
export type Executor = 'human' | 'ai';
export type TaskType = 'meeting' | 'work' | 'communication' | 'review' | 'other' | 'decision';
export type EventType = 'meeting' | 'deadline' | 'travel' | 'meal' | 'focus' | 'personal' | 'other';

export type GoalExtractionReasonCode =
  | 'explicit_request'
  | 'implicit_commitment'
  | 'calendar_attendance'
  | 'decision_required'
  | 'follow_up'
  | 'user_manual';

export type GoalExtractionSourceType = 'gmail' | 'slack' | 'calendar' | 'screenshot' | 'manual';

export interface GoalExtractionSourceRef {
  source_type: GoalExtractionSourceType;
  source_ref: string;
  excerpt?: string;
  excerpt_offset?: number;
  observed_at?: string;
}

export type GoalSourceRefType = GoalExtractionSourceType | 'unknown' | 'mixed' | 'mcp' | 'other';

export interface GoalSourceRef {
  source_type: GoalSourceRefType;
  source_ref: string;
  excerpt?: string;
}

export type IntakeDecisionSourceType = GoalExtractionSourceType | 'mcp' | 'other';
export type IntakeDecisionConfidence = 'high' | 'medium' | 'low';
export type IntakeDecisionClass =
  | 'created_goal'
  | 'linked_existing_goal'
  | 'merge_candidate'
  | 'skipped_duplicate'
  | 'skipped_not_actionable'
  | 'skipped_low_confidence'
  | 'deferred_needs_user_judgment'
  | 'deferred_needs_more_context';
export type IntakeDecisionOutcome =
  | 'create_goal'
  | 'nominate_goal'
  | 'request_clarification'
  | 'record_reference'
  | 'link_existing'
  | 'mark_duplicate'
  | 'skip';
export type IntakeDecisionOutcomeSource = 'explicit' | 'write_derived' | 'compatibility_derived';

export interface IntakeDecisionSourceRef {
  source_type: IntakeDecisionSourceType;
  source_ref: string;
  excerpt?: string | null;
  excerpt_offset?: number | null;
  observed_at?: string | null;
}

export interface PendingIntakeAdmissionCandidate {
  decision_id: string;
  source_item_ref: string;
  decision_outcome: IntakeDecisionOutcome;
  decision_outcome_source: IntakeDecisionOutcomeSource;
  decision_class: Extract<IntakeDecisionClass, 'deferred_needs_user_judgment' | 'deferred_needs_more_context'>;
  reason_code: string;
  reason_summary: string;
  confidence: IntakeDecisionConfidence;
  source_refs: IntakeDecisionSourceRef[];
  provenance_refs: string[];
  candidate_refs: string[];
  deferred_packet_ref: string | null;
  resolution_surface_read?: Record<string, unknown> | null;
  kernel_decision_body?: Record<string, unknown> | null;
  decided_at: string;
  created_at: string;
  admission: {
    admission_decision: string;
    decision_outcome?: IntakeDecisionOutcome;
    decision_outcome_source?: IntakeDecisionOutcomeSource;
    rationale_summary: string;
    source_refs: IntakeDecisionSourceRef[];
    match_confidence: IntakeDecisionConfidence;
    [key: string]: unknown;
  };
}

export interface DodItem {
  text: string;
  done: boolean;
}

export type GoalDodEvidenceType =
  | 'sent_message'
  | 'reply'
  | 'rsvp'
  | 'record'
  | 'event'
  | 'file'
  | 'draft';

export type GoalDodWhoType = 'self' | 'other_person' | 'external_system' | 'ai';
export type GoalDodDoneCondition = 'transmitted' | 'agreed' | 'registered' | 'attended' | 'decided' | 'created';
export type GoalDodItemTracking = 'active' | 'retired';
export type GoalDodItemSatisfaction = 'pending' | 'observed' | 'satisfied';
export type GoalDodItemConfirmedBy = 'ai' | 'user';

export interface GoalDodEvidence {
  system: string;
  type: GoalDodEvidenceType;
  detail?: string;
}

export interface GoalDodItemEvidenceRecord {
  source: 'signal' | 'decision' | 'ai_result' | 'user' | 'system';
  kind: string;
  recorded_at: string;
  event_id?: string;
  event_idempotency_key?: string;
  summary?: string;
  metadata?: Record<string, unknown>;
}

export interface GoalDodItem {
  type: 'action' | 'state';
  what: string;
  who: string;
  who_type: GoalDodWhoType;
  evidence: GoalDodEvidence;
  quality?: string;
  derived_condition?: GoalDodDoneCondition | null;
  item_key?: string;
  tracking?: GoalDodItemTracking;
  satisfaction?: GoalDodItemSatisfaction;
  confirmed_by?: GoalDodItemConfirmedBy | null;
  confirmed_at?: string | null;
  satisfaction_evidence?: GoalDodItemEvidenceRecord[];
  satisfaction_updated_at?: string;
  source_piece_keys?: string[];
  /** #3560 stage 2: where the meeting is held is ready, read from Jev's answers and the tasks (not stored). */
  readiness?: GoalDodReadinessLine[];
}

export interface GoalDodReadinessLine {
  kind: string;
  what: string;
  /** done: ready / waiting: on the other party / open: still to do or decide. */
  state: 'done' | 'waiting' | 'open';
}

export interface DecisionOption {
  label: string;
  value?: string;
  reason: string;
  recommended?: boolean;
  continuation_preview?: {
    type: 'create_followup_goal' | 'complete_source_goal' | 'keep_open';
    title?: string;
    dod?: string;
    deadline?: string | null;
  };
}

export interface DecisionMaterial {
  title: string;
  content: string;
}

export type JudgmentReadiness = "none" | "prep_needed" | "awaiting_user_judgment" | "user_selected";
export type JudgmentOptionConfidence = "source_backed" | "inferred" | "weak";
export type JudgmentDimensionState = "known" | "unknown" | "needs_refresh" | "not_applicable";

export interface JudgmentOptionCandidateRead {
  key: string;
  label: string;
  value: string | null;
  reason: string | null;
  confidence: JudgmentOptionConfidence;
  recommended: boolean;
  terminal?: boolean;
}

export interface JudgmentDimensionRead {
  key: string;
  label: string;
  state: JudgmentDimensionState;
  summary: string | null;
  source_refs?: string[];
  preparation_action?: string;
  /** #2859: the calendar checks behind a `calendar_check` dimension. */
  calendar_checks?: JudgmentCalendarCheckRead[];
}

/** #2859: one calendar read for the time of a judgment, and what overlaps it. */
export interface JudgmentCalendarCheckRead {
  result_ref: string;
  checked_at: string;
  start_at: string;
  end_at: string;
  /** false when the check stated no time and the matter's time stands in for it. */
  time_stated?: false;
  overlapping: Array<{ title: string | null; start_at: string; end_at: string }>;
}

/** #2422 stage 2: a canonical judgment packet with the coordinate of its judgment. */
export interface CanonicalJudgmentPacketEntry {
  condition_ref: string;
  axis: string;
  packet: JudgmentPacketRead;
}

export interface JudgmentPacketRead {
  readiness: JudgmentReadiness;
  question: string | null;
  option_candidates: JudgmentOptionCandidateRead[];
  dimensions: JudgmentDimensionRead[];
  pre_judgment_actions: string[];
  post_selection_follow_through_hints: string[];
  selection_snapshot_token?: string;
  selected_option_key?: string | null;
  /** #2951: a place card with no place written offers a box to type it in (sent as `text` with this option). */
  text_input?: { option_key: string; option_value: string; placeholder: string; value: string | null };
}

/**
 * The #1886 derived readback is versioned by its containing simplified intake
 * surface. Clients may display the exact bounded issue codes, but never
 * reinterpret them as a lifecycle or task state.
 */
export type SemanticCompletionState = 'open' | 'closed' | 'unresolved';

export type SemanticCompletionIssueCode =
  | 'settlement_pointer_missing'
  | 'settlement_conflict'
  | 'settlement_malformed'
  | 'stale_input'
  | 'binding_target_missing'
  | 'binding_target_conflict'
  | 'time_scoped_coordinate_invalid'
  | 'participation_dependency_unresolved'
  | 'target_alignment_unresolved'
  | 'post_binding_revision_not_ready'
  | 'post_binding_revision_failed'
  | 'post_binding_revision_ungrounded';

export interface SemanticCompletionRead {
  state: SemanticCompletionState;
  participating_condition_refs: string[];
  unresolved_coordinate_refs: string[];
  issue_codes: SemanticCompletionIssueCode[];
  goal_lens_version: string | null;
  reality_lens_version: string | null;
}

export interface SimplifiedGoalActionIntakeRead {
  action_state_actor: {
    action_state: "none" | "todo" | "waiting" | "review" | "done";
    action_actor: "me" | "other" | "ai" | "system";
    derivation_source: string;
    derivation_reasons: string[];
    ambiguity_reasons: string[];
    resolver_version: string;
  };
  ui: {
    ui_bucket: string | null;
    bucket_label_ja: string | null;
    cta_families: string[];
    normal_surface_visibility: "show" | "hide" | "history_only";
    derivation_reasons: string[];
    resolver_version: string;
  };
  judgment_packet: JudgmentPacketRead | null;
  /** #2422 stage 2: one packet per judgment, present when the Goal declares several. */
  judgment_packets?: CanonicalJudgmentPacketEntry[];
  /** #2361: the Goal's latest review could not run. */
  review_unavailable?: {
    reason: 'llm_unavailable' | 'contract_violation' | 'action_reflection_failed';
    /** #2631: the job tries again, or the principal's next input resumes it. */
    recovery: 'retrying' | 'waiting_for_principal';
    failed_at: string;
  } | null;
  /** The Goal's review is running or about to run: its work is being rebuilt. */
  review_in_progress?: boolean;
  /** #2086: changes the review proposes after a new message, waiting for the principal. */
  review_change_proposals?: ReviewChangeProposal[];
  /** #2970: whose turn the Goal is on (src/services/goal-turn.ts). */
  waiting_on?: {
    who: 'principal' | 'counterparty' | 'someone_else' | 'nobody';
    name: string | null;
    label: string;
    read_at: string;
    basis?: 'principal' | 'review' | 'plan';
    what?: string | null;
    since?: string | null;
  };
  /** #2086 S6 stage A: one line per follow-up message, oldest first. */
  follow_up_history?: Array<{ at: string; from: string | null; sent_by_principal: boolean; line: string }>;
  semantic_completion?: SemanticCompletionRead | null;
  action_runtime_recovery?: Array<{
    proposal_instance_id: string;
    subtask_id: string | null;
    status: string;
    reason_code: string;
    retryable: boolean;
    recorded_at: string | null;
    recovery_action: "retry_source_producer" | "retry_projection";
  }>;
}

export interface GrmaMaterializationSemanticReadback {
  contract_version: 'grma_materialization_semantic_readback.v1';
  source_status: 'pending' | 'completed' | 'failed' | 'degraded' | 'clarification_needed' | null;
  handoff_ready: boolean;
  materialization_state: 'pending' | 'materialized' | 'blocked' | 'no_work' | 'unsupported' | 'invalid' | 'producer_failed' | null;
  materialization_success: boolean;
  user_landing: 'optimizing' | 'warning' | 'failed' | 'empty' | 'complete' | null;
  terminal: boolean;
}

export interface GrmaMaterializationReadback {
  contract_version: 'grma_materialization_client_readback.v1';
  source_contract_version: string;
  state: 'materialized' | 'blocked' | 'no_work' | 'unsupported' | 'invalid' | 'producer_failed';
  disposition: string;
  semantic: GrmaMaterializationSemanticReadback;
  terminal: true;
  legacy_fallback_attempted: false;
  can_retry: boolean;
  stop_reason?: 'generation_failed' | 'input_missing' | 'external_wait' | 'dependency_wait' | 'not_needed' | null;
  stop_reason_codes?: Record<string, string[]>;
  recorded_at: string;
  materialized_subtasks_count: number;
  skipped_cells_count: number;
  skipped_cell_reasons: string[];
  evidence_issues: string[];
  waiting_readbacks: GrmaWaitingReadback[];
}

export interface GrmaWaitingReadback {
  proposal_ref: string;
  proposal_key: string | null;
  source_condition_refs: string[];
  waiting_summary: string;
  expected_signal: string | null;
  depends_on_proposal_keys: string[];
}

export interface GrmaLensReadback {
  contract_version: 'grma_lenses_client_readback.v1';
  versions: {
    goal_lens_version: string | null;
    reality_lens_version: string | null;
    missing_piece_lens_goal_lens_version: string | null;
    missing_piece_lens_reality_lens_version: string | null;
  };
  reality_lens: {
    present: boolean;
    lens_ref: string | null;
    assessment_count: number;
    condition_count: number;
    source_refs_count: number;
    evidence_refs_count: number;
    status_counts: Record<string, number>;
    confirmable_by_counts: Record<string, number>;
  };
  missing_piece_lens: {
    present: boolean;
    lens_version: string | null;
    cell_count: number;
    condition_count: number;
    derived_at: string | null;
    need_kind_counts: Record<string, number>;
    settlement_status_counts: Record<string, number>;
    settlement_subject_counts: Record<string, number>;
  };
  materialization: {
    state: GrmaMaterializationReadback['state'] | null;
    disposition: string | null;
    recorded_at: string | null;
    can_retry: boolean;
    materialized_subtasks_count: number;
    skipped_cells_count: number;
    skipped_reason_counts: Record<string, number>;
    evidence_issues: string[];
  };
}

/** #2732: the principal's answer to 「誰が進めますか」. */
export type ActorQuestionAnswer =
  | { actor: 'principal' | 'external_party' }
  | { actor: 'teammate'; teammate_user_id: string };

/** #2732: 「誰が進めますか」 on a Goal, from the Goal list (`actor_questions`). */
export interface GoalActorQuestions {
  conditions: Array<{ condition_ref: string; label: string; answer?: ActorQuestionAnswer | null }>;
  held_subtask_ids: string[];
  teammates: Array<{ user_id: string; name: string }>;
}

export interface Goal {
  /** #2732: 「誰が進めますか」, unanswered and answered; null when none. */
  actor_questions?: GoalActorQuestions | null;
  /** #2494: one row per lens condition; null on Goals that keep the string DoD edit. */
  dod_rows?: GoalDodRow[] | null;
  /** #2494: the lens version a DoD edit carries. */
  lens_version?: number | null;
  id: string;
  workspace_id: string;
  created_by: string;
  title: string;
  description: string | null;
  dod: string | null;
  deadline: string | null;
  context: string | null;
  ai_summary: string | null;
  status: 'active' | 'done' | 'deleted';
  lifecycle_state: GoalLifecycleState;
  intent: 'do_it_later' | 'ask_later' | 'ask_ai' | null;
  source_url: string | null;
  screenshot_path: string | null;
  source_screenshot_id: string | null;
  priority: Priority | null;
  done_at: string | null;
  start_at: string | null;
  deadline_has_time: boolean;
  start_at_has_time: boolean;
  /** #2320: the deadline and event time as the server's one reader returns them. */
  dates?: GoalDatesRead | null;
  /**
   * #2282: the tasks were made without asking the judgment again with the
   * current question set. `plan_read` false: they were made without it.
   */
  judgment_refresh_unavailable?: { reason: 'not_mail' | 'reconnect' | 'mail_gone' | 'temporary'; plan_read: boolean } | null;
  created_at: string;
  updated_at: string;
  created_from: string | null;
  extraction_reason_code?: GoalExtractionReasonCode | null;
  extraction_confidence?: number | null;
  extraction_source_refs?: GoalExtractionSourceRef[] | null;
  source_refs?: GoalSourceRef[] | null;
  source_type: string;
  subtask_generation_status: 'pending' | 'success' | 'failed' | null;
  fast_goal_initial_optimization?: FastGoalInitialOptimizationReadback | null;
  goal_contract_readback?: GoalContractReadbackV1 | null;
  metadata: Record<string, unknown> | null;
  facts: Record<string, unknown> | null;
  grma_materialization?: GrmaMaterializationReadback | null;
  grma_materialization_semantic?: GrmaMaterializationSemanticReadback | null;
  grma_lenses?: GrmaLensReadback | null;
  goal_dod_items?: GoalDodItem[] | null;
  reconcile_status?: 'synced' | 'drifted' | null;
  simplified_action_intake?: SimplifiedGoalActionIntakeRead | null;
}

export type GoalLifecycleState = 'candidate' | 'active' | 'done' | 'dismissed' | 'deleted';

export type FastGoalInitialOptimizationStatus =
  | 'queued'
  | 'running'
  | 'stale'
  | 'completed'
  | 'failed'
  | 'degraded';
export type FastGoalInitialOptimizationResult = 'updated' | 'no_changes' | 'completed_with_warnings' | 'failed';

export interface FastGoalInitialOptimizationReadback {
  version?: 2 | 'fast-goal-initial-optimization-v2';
  status: FastGoalInitialOptimizationStatus;
  generation_ref?: string;
  pipeline?: string;
  stage?: string;
  queued_at?: string | null;
  semantic_clock_at?: string | null;
  stale_at?: string | null;
  running_at?: string | null;
  failed_at?: string | null;
  degraded_at?: string | null;
  attempt?: number;
  replayable?: boolean;
  explicit_children?: boolean;
  expected_children_count?: number | null;
  result: FastGoalInitialOptimizationResult | null;
  reason_code?: string | null;
  completed_at: string | null;
  warning_codes: string[];
  message_key?: string;
  /** #2660: the authored title and conditions while the Actions are still being proposed. */
  creation_preview?: FastGoalCreationPreview;
}

export interface FastGoalCreationPreview {
  title: string;
  conditions: string[];
}

export type GoalContractReadbackStatus = 'pending' | 'completed' | 'failed' | 'degraded' | 'clarification_needed';

export interface GoalContractReadbackV1 {
  contract_version: 'goal_contract_readback.v1';
  status: GoalContractReadbackStatus;
  updated_at: string;
  reason_code?: string;
  reason_summary?: string;
  trace_version?: string;
  goal_contract_ref?: string;
  draft_count?: number;
  goal_lens_version?: string;
  evidence_set_version?: string;
}

export interface Subtask {
  id: string;
  goal_id: string;
  title: string;
  description: string | null;
  executor: Executor;
  task_type: TaskType;
  assigned_to: string | null;
  ai_action: string | null;
  priority: Priority;
  estimated_minutes: number | null;
  depends_on: string[] | null;
  order_index: number;
  status: SubtaskStatus;
  deadline: string | null;
  deadline_inherited: boolean;
  dod_items: DodItem[];
  decision_options: DecisionOption[] | null;
  decision_result: string | null;
  decision_materials: DecisionMaterial[] | null;
  rejection_feedback?: string | null;
  source: string;
  done_at: string | null;
  invalidated_reason: SubtaskInvalidatedReason | null;
  invalidated_at: string | null;
  terminal_reason: SubtaskTerminalReason | null;
  terminal_at: string | null;
  superseded_by_subtask_id: string | null;
  blocked_by: string | null;
  source_piece: string | null;
  piece_phase?: 'before' | 'after' | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface AiExecutionResult {
  id: string;
  subtask_id: string;
  action_type: string;
  input_context: Record<string, unknown>;
  result_content: string;
  result_metadata: Record<string, unknown>;
  status: 'success' | 'error' | 'pending';
  error_message: string | null;
  executed_at: string;
  approved_at: string | null;
  approved_by: string | null;
  dispatched_at: string | null;
}

export interface SubtaskWithResult extends Subtask {
  ai_result?: AiExecutionResult | null;
}

export interface GoalEventSnapshot {
  title?: string;
  start_at?: string;
  end_at?: string | null;
  is_all_day?: boolean;
  location?: string | null;
  description?: string | null;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  event_type?: EventType;
  category?: 'business' | 'internal' | 'private' | 'block';
  source?: string | null;
  calendar_event_id?: string | null;
}

export interface CalendarEvent {
  id: string;
  workspace_id: string;
  title: string;
  start_at: string;
  end_at: string | null;
  is_all_day: boolean;
  location: string | null;
  description: string | null;
  status: 'confirmed' | 'tentative' | 'cancelled';
  calendar_event_id: string | null;
  source: string;
  done_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_app: boolean;
  is_tentative_hold: boolean;
  holding_expires_at: string | null;
  category: 'business' | 'internal' | 'private' | 'block';
  event_type: EventType;
}

export interface GoalEventSurfaceItem extends CalendarEvent {
  link_id: string;
  link_status: 'suggested' | 'linked';
  is_primary: boolean;
}

export interface GoalEventSurface {
  primary_linked: GoalEventSurfaceItem | null;
  related_events: GoalEventSurfaceItem[];
  suggested_links: GoalEventSurfaceItem[];
  tentative_holds: GoalEventSurfaceItem[];
}

export interface GoalWithSubtasks extends Goal {
  decisions: SubtaskWithResult[];
  subtasks: SubtaskWithResult[];
  screenshot_url: string | null;
  linked_event: CalendarEvent | null;
  goal_event_surface: GoalEventSurface;
}

// ============================================
// Frontend-specific types
// ============================================

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  json?: Record<string, unknown>;
  headers?: Record<string, string>;
  body?: FormData | string;
  signal?: AbortSignal;
  // A 401 answers the sign-in itself (wrong password, code needed), not an expired session.
  signIn?: boolean;
}

export interface UploadEntry {
  id: string;
  file: File;
  dataUrl: string | null;
  status: 'pending' | 'analyzing' | 'done';
  summary: string | null;
  goals: Array<{ id: string; title: string }>;
  abortController: AbortController | null;
}

// ============================================
// Workspace (issue #1191 / #1239)
// ============================================

export interface Workspace {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
}

export type WorkspaceMemberRole = 'owner' | 'admin' | 'member' | 'guest';
export type WorkspaceMembershipStatus = 'active' | 'inactive' | 'deleted';
export type WorkspaceInvitationRole = Exclude<WorkspaceMemberRole, 'owner'>;
export type WorkspaceInvitationStatus = 'pending' | 'consumed' | 'revoked' | 'expired';

export interface WorkspaceMembership {
  id: string;
  workspace_id: string;
  user_id: string;
  role: WorkspaceMemberRole;
  status: WorkspaceMembershipStatus;
  deleted_at: string | null;
  created_at: string;
}

export interface WorkspacePermissions {
  can_manage_members: boolean;
  can_view_admin_profiles: boolean;
}

export interface WorkspaceMemberProfilePublic {
  display_name: string | null;
}

export interface WorkspaceMemberProfileAdmin {
  id: string;
  user_id: string;
  display_names: string[];
  display_name: string | null;
  role: string | null;
  interests: string[] | null;
  current_goals: string[] | null;
  location: string | null;
  timezone: string | null;
  language: string | null;
  updated_at: string;
  last_sync_at: string | null;
  last_sync_summary: string | null;
  last_sync_status: string | null;
}

export interface WorkspaceMemberRecord {
  id: string;
  user_id: string;
  role: WorkspaceMemberRole;
  status: WorkspaceMembershipStatus;
  deleted_at: string | null;
  created_at: string;
  profile: WorkspaceMemberProfilePublic | WorkspaceMemberProfileAdmin;
}

export type WorkspaceInvitationAcceptanceMode = 'delete_existing' | 'migrate_existing';

// Response of POST /api/workspaces/invitations/inspect: the accept screen preview.
export interface WorkspaceInvitationInspection {
  invitation: {
    id: string;
    workspace_id: string;
    workspace_name: string;
    inviter_name: string | null;
    role: WorkspaceInvitationRole;
    expires_at: string;
  };
  existing_data: {
    goal_count: number;
    event_count: number;
  };
  requires_mode_choice: boolean;
}

export interface WorkspaceInvitationPublic {
  id: string;
  workspace_id: string;
  target_email: string;
  target_email_normalized: string;
  role: WorkspaceInvitationRole;
  status: WorkspaceInvitationStatus;
  created_by_user_id: string;
  consumed_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string;
  consumed_at: string | null;
  revoked_at: string | null;
}

export interface DatePickerOptions {
  currentISO: string | null;
  minISO?: string;
  rangeFrom?: string | null;
  rangeTo?: string | null;
  hint?: string;
  clearable?: boolean;
  enableTime?: boolean;
  currentTime?: string;
  onSave: (value: string | null | { dateISO: string | null; timeStr: string; hasTime: boolean }) => void;
}

// ============================================
// Error helper
// ============================================

export function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message);
  }
  return String(err);
}

/** Mirrors `GoalDatesRead` in src/lib/goal-dates.ts. */
export interface GoalDatesRead {
  deadline: {
    at: string;
    date: string;
    has_time: boolean;
    timezone: string;
    /** No deadline was read; this one comes from the profile preference. Shown with 「仮」 (#3064). */
    provisional: boolean;
    overdue: boolean;
  } | null;
  event: {
    start_at: string;
    end_at: string | null;
    all_day: boolean;
    /** #3060: false when the source gave the date but not the start time. */
    has_time?: boolean;
    date: string;
    timezone: string;
    source: 'calendar' | 'source';
  } | null;
  /** Decision 13: the invitation's repeat rule could not be read; only the first occurrence is shown. */
  series_rule_unread?: true;
}

/** #2086: one change the review proposes; nothing changes until the principal answers. */
export interface ReviewChangeProposal {
  proposal_ref: string;
  kind: 'withdraw_condition' | 'add_condition' | 'rewrite_condition';
  condition_ref?: string;
  condition?: string;
  message?: { at: string; from: string | null };
  /** #2086 S6 stage B: which written value a later message replaced (rewrite_condition). */
  replaced?: 'date_time' | 'place' | 'method' | 'person' | 'other_value';
  /** #2086 S6 stage B: the drafted criterion sentence, not yet confirmed by the principal. */
  draft?: string;
  question: string;
}
